<script setup lang="ts">
/**
 * Table cell for a client's debt, derived from `latest_balance` (balance as
 * of the 1st of the current month plus payments made so far this month; see
 * `PersonRspDTO.latest_balance`).
 *
 * Unlike `MoneyCell`, this never shows a positive amount: a non-negative
 * `amount` means last month's debt is closed, so the cell always renders "0"
 * in `--color-money-positive` (green) for that case, regardless of how much
 * credit the client is actually carrying. A negative `amount` is genuine
 * debt and renders as-is in `--color-money-negative` (red).
 */
import { computed } from 'vue'
import { formatMoney } from '@/utils/formatMoney'

const props = withDefaults(
  defineProps<{
    /** `latest_balance` in minor currency units. Omit or null when unknown. */
    amount?: number | null
    /** Shown when amount is absent. Default "–". */
    emptyPlaceholder?: string
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
  }>(),
  {
    emptyPlaceholder: '–',
  },
)

const hasAmount = computed(
  () => props.amount !== undefined && props.amount !== null && !Number.isNaN(props.amount),
)

const isDebt = computed(() => hasAmount.value && props.amount! < 0)

const result = computed(() =>
  hasAmount.value ? formatMoney({ amount: isDebt.value ? props.amount! : 0 }) : null,
)

const colorStyle = computed(() => {
  if (!hasAmount.value) return {}
  return isDebt.value
    ? { color: 'var(--color-money-negative)' }
    : { color: 'var(--color-money-positive)' }
})
</script>

<template>
  <td
    class="font-tufte debt-cell"
    :style="{
      ...colorStyle,
      ...(props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : {}),
    }"
  >
    <span v-if="result">{{ result.formatted }}</span>
    <span v-else class="text-tufte-muted">{{ emptyPlaceholder }}</span>
  </td>
</template>

<style scoped>
.debt-cell {
  text-align: center;
}
</style>
